import { bookingsVariables } from './variables'
import type { BookingsFilters, BookingsVariables } from './variables'
import type {
  Appointment,
  Company,
  DailyNote,
  DoctorSlotsResponse,
  Document,
  Exam,
  JobRole,
  Patient,
  SiteInspection,
  UpdateAppointment,
  UpsertDailyNote,
  User,
} from '@shared/schemas'
import type { PageProps, QueryDefinition } from '@/core'
import type { AxiosRequestConfig } from 'axios'
import type { BookingsFormValues } from './form'
import { AVAILABILITY_KEYS, USER_KEYS } from '@/features/users'
import { JOB_ROLES_KEYS } from '@/features/job-roles'

export const BOOKINGS_KEYS = {
  all: ['appointments'] as const,
  lists: () => [...BOOKINGS_KEYS.all, 'list'].join('/'),
  list: (filters: BookingsFilters) =>
    `${BOOKINGS_KEYS.lists()}/${JSON.stringify(filters)}`,
  details: () => [...BOOKINGS_KEYS.all, 'detail'].join('/'),
  detail: (id: string) => [BOOKINGS_KEYS.details(), id].join('/'),
  search: (query: string) => [...BOOKINGS_KEYS.all, 'search', query].join('/'),
  doctor: (doctorId: string) =>
    [...BOOKINGS_KEYS.all, 'doctor', doctorId].join('/'),
  company: (companyId: string) =>
    [...BOOKINGS_KEYS.all, 'company', companyId].join('/'),
  summary: {
    all: () => [...BOOKINGS_KEYS.all, 'summary'] as const,
    daily: (date: string) =>
      [...BOOKINGS_KEYS.summary.all(), 'daily', date].join('/'),
  },
}

export interface DailySummary {
  date: string
  appointments: Array<Appointment>
  notes?: {
    work?: DailyNote
    sport?: DailyNote
  }
}

export interface PreAcceptanceData {
  appointments: Array<Appointment>
  patients: Array<Patient>
  companies: Array<Company>
  doctors: Array<User>
  exams: Array<Exam>
}

export interface PatientExportDetails {
  id: string
  dateOfBirth: string
  placeOfBirth: string
  jobRoleId?: string
}

export interface IdoneityUploadInput {
  appointmentId: string
  file: File
  notes: string
}

export interface DailyDocumentsUploadInput {
  agendaDate: string
  files: Array<File>
}

export interface BookingActionResponse {
  success: boolean
  message: string
}

export type BookingsQueryDefinition = [
  QueryDefinition<'appointments', 'query', void, Array<Appointment>>,
  QueryDefinition<
    'updateAppointment',
    'mutation',
    Partial<UpdateAppointment & { id: string }>,
    Appointment
  >,
  QueryDefinition<'doctors', 'query', void, Array<User>>,
  QueryDefinition<'dailySummary', 'query', void, DailySummary>,
  QueryDefinition<
    'multiDoctorSlots',
    'query',
    void,
    Array<DoctorSlotsResponse>
  >,
  QueryDefinition<'deleteAppointment', 'mutation', string, void>,
  QueryDefinition<
    'toggleDoctorAvailability',
    'mutation',
    { uid: string; active: boolean },
    User
  >,
  QueryDefinition<'upsertDailyNote', 'mutation', UpsertDailyNote, DailyNote>,
  QueryDefinition<'deleteDailyNote', 'mutation', void, void>,
  QueryDefinition<'preAcceptanceData', 'query', void, PreAcceptanceData>,
  QueryDefinition<
    'monthStatus',
    'query',
    void,
    Record<string, ExtendedMonthStatus>
  >,
  QueryDefinition<'dailyDocuments', 'query', void, Array<Document>>,
  QueryDefinition<
    'patientsForExport',
    'query',
    void,
    Array<PatientExportDetails>
  >,
  QueryDefinition<'jobRolesForExport', 'query', void, Array<JobRole>>,
  QueryDefinition<
    'siteInspectionsForExport',
    'query',
    void,
    Array<SiteInspection>
  >,
  QueryDefinition<'companyUsers', 'query', void, Array<User>>,
  QueryDefinition<
    'updateAppointmentStatus',
    'mutation',
    { id: string; status: string },
    Appointment
  >,
  QueryDefinition<
    'updateAppointmentAcceptance',
    'mutation',
    { id: string; acceptance: boolean },
    Appointment
  >,
  QueryDefinition<
    'removeAppointmentIdoneity',
    'mutation',
    { id: string },
    { success: boolean }
  >,
  QueryDefinition<
    'markAllAppointmentsCompleted',
    'mutation',
    { date: string },
    { success: boolean; updatedCount: number; skippedCount: number }
  >,
  QueryDefinition<
    'sendPatientReminder',
    'mutation',
    { id: string },
    BookingActionResponse
  >,
  QueryDefinition<
    'sendCompanyReminder',
    'mutation',
    { id: string; scope?: 'single' | 'all_company_for_date' },
    BookingActionResponse
  >,
  QueryDefinition<
    'sendCompanyConfirmation',
    'mutation',
    { id: string; scope?: 'single' | 'all_company_for_date' },
    BookingActionResponse
  >,
  QueryDefinition<
    'sendPatientDataReminder',
    'mutation',
    { id: string },
    BookingActionResponse
  >,
  QueryDefinition<
    'sendCompanyConfirmationRequest',
    'mutation',
    { id: string; scope?: 'single' | 'all_company_for_date' },
    BookingActionResponse
  >,
  QueryDefinition<
    'sendNoShowNotification',
    'mutation',
    { id: string },
    BookingActionResponse
  >,
  QueryDefinition<
    'sendDoctorAbsenceNotification',
    'mutation',
    { doctorId: string; date: string; appointmentId?: string },
    BookingActionResponse
  >,
  QueryDefinition<'uploadIdoneity', 'mutation', IdoneityUploadInput, unknown>,
  QueryDefinition<
    'uploadDailyDocuments',
    'mutation',
    DailyDocumentsUploadInput,
    unknown
  >,
]

async function uploadIdoneity(
  url: string,
  method: string,
  input: IdoneityUploadInput,
  headers: AxiosRequestConfig['headers'],
) {
  const formData = new FormData()
  formData.append('file', input.file)
  formData.append(
    'data',
    JSON.stringify({ notes: input.notes, type: 'idoneita' }),
  )
  const requestHeaders = new Headers(headers as HeadersInit)
  requestHeaders.delete('Content-Type')
  const response = await fetch(
    url.replace(':appointmentId', input.appointmentId),
    {
      method,
      headers: requestHeaders,
      body: formData,
    },
  )
  if (!response.ok) throw new Error('Idoneity submission failed')
  return response.json() as Promise<unknown>
}

async function uploadDailyDocuments(
  url: string,
  method: string,
  input: DailyDocumentsUploadInput,
  headers: AxiosRequestConfig['headers'],
) {
  const formData = new FormData()
  input.files.forEach((file) => formData.append('files', file))
  formData.append(
    'data',
    JSON.stringify({
      recipientIds: [],
      notes: `Documento caricato per la giornata ${input.agendaDate}`,
      sendEmail: false,
      type: 'documenti',
      agendaDate: input.agendaDate,
    }),
  )
  const requestHeaders = new Headers(headers as HeadersInit)
  requestHeaders.delete('Content-Type')
  const response = await fetch(url, {
    method,
    headers: requestHeaders,
    body: formData,
  })
  if (!response.ok) throw new Error('Upload failed')
  return response.json() as Promise<unknown>
}

export function createBookingsQueries(userData?: User) {
  const queries: NonNullable<
    PageProps<
      BookingsFormValues,
      BookingsQueryDefinition,
      BookingsVariables
    >['queries']
  > = [
    {
      key: 'appointments',
      type: 'query',
      queryConfig: ({ get, set }) => {
        const filters = get('state', 'filters', bookingsVariables.filters)

        const params = new URLSearchParams()
        if (filters.selectedDate) params.append('date', filters.selectedDate)
        let doctorId = filters.selectedDoctorId
        if (filters.userId) {
          doctorId = filters.userId
        }
        if (doctorId) params.append('doctorId', doctorId)

        if (filters.selectedLocation)
          params.append('location', filters.selectedLocation)
        if (filters.selectedVisitType)
          params.append('visitType', filters.selectedVisitType)
        if (filters.selectedStatus)
          params.append('status', filters.selectedStatus)
        if (filters.selectedPatientId?.trim())
          params.append('patientId', filters.selectedPatientId.trim())
        if (filters.selectedCompanyId?.trim())
          params.append('companyId', filters.selectedCompanyId.trim())

        return {
          queryKey: [
            `appointments?${filters.selectedDate}`,
            BOOKINGS_KEYS.list(filters),
          ],
          endpoint: ['api', `appointments?${params.toString()}`],
          enabled: true,
          onDataChanged: (appointments: Array<Appointment> | undefined) => {
            set('state')(
              'exportPatientIds',
              Array.from(
                new Set(
                  (appointments ?? [])
                    .map((item) => item.patientId)
                    .filter(Boolean),
                ),
              ),
            )
          },
        }
      },
    },
    {
      key: 'updateAppointment',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const filters = get('state', 'filters', bookingsVariables.filters)
        const { selectedDate } = filters
        return {
          endpoint: ['api', `appointments/:id`],
          method: 'PUT',
          queryKeyToInvalidate: [
            AVAILABILITY_KEYS.slots.multiDoctor(selectedDate),
            BOOKINGS_KEYS.summary.daily(selectedDate),
            BOOKINGS_KEYS.list(filters),
          ],
        }
      },
    },
    {
      key: 'doctors',
      type: 'query',
      queryConfig: {
        queryKey: [USER_KEYS.doctors.all()],
        endpoint: ['api', 'users/doctors/all'],
        enabled: true,
      },
    },
    {
      key: 'dailySummary',
      type: 'query',
      queryConfig: ({ get }) => {
        const { selectedDate } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        return {
          queryKey: [BOOKINGS_KEYS.summary.daily(selectedDate)],
          endpoint: ['api', `appointments/summary/daily/${selectedDate}`],
          enabled:
            !!selectedDate &&
            (userData?.role === 'admin' || userData?.role === 'doctor'),
        }
      },
    },
    {
      key: 'multiDoctorSlots',
      type: 'query',
      queryConfig: ({ get }) => {
        const { selectedDate } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        return {
          queryKey: [AVAILABILITY_KEYS.slots.multiDoctor(selectedDate)],
          endpoint: ['api', `availability/slots?date=${selectedDate}`],
          enabled: !!selectedDate,
        }
      },
    },
    {
      key: 'deleteAppointment',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const filters = get('state', 'filters', bookingsVariables.filters)
        const { selectedDate } = filters
        return {
          endpoint: ['api', `appointments`],
          method: 'DELETE',
          queryKeyToInvalidate: [
            AVAILABILITY_KEYS.slots.multiDoctor(selectedDate),
            BOOKINGS_KEYS.summary.daily(selectedDate),
            BOOKINGS_KEYS.list(filters),
          ],
        }
      },
    },
    {
      key: 'toggleDoctorAvailability',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'users/doctors/:uid/availability'],
        method: 'PUT',
      },
    },
    {
      key: 'upsertDailyNote',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const { selectedDate } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        return {
          endpoint: ['api', `schedule/daily-note`],
          method: 'POST',
          queryKeyToInvalidate: [BOOKINGS_KEYS.summary.daily(selectedDate)],
        }
      },
    },
    {
      key: 'deleteDailyNote',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const { selectedDate, dailyNoteCategory } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        return {
          endpoint: [
            'api',
            `schedule/daily-note/${selectedDate}/${dailyNoteCategory}`,
          ],
          method: 'DELETE',
          queryKeyToInvalidate: [BOOKINGS_KEYS.summary.daily(selectedDate)],
        }
      },
    },
    {
      key: 'preAcceptanceData',
      type: 'query',
      queryConfig: ({ get }) => {
        const { selectedDate } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        return {
          queryKey: [`preacceptance-data/${selectedDate}`],
          endpoint: [
            'api',
            `appointments/export/pre-acceptance?date=${selectedDate}`,
          ],
          enabled: !!selectedDate && userData?.role === 'admin',
        }
      },
    },
    {
      key: 'monthStatus',
      type: 'query',
      queryConfig: ({ get }) => {
        const { selectedDate, selectedDoctorId } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        const month = selectedDate.slice(0, 7)
        const queryParams = selectedDoctorId
          ? `?doctorId=${selectedDoctorId}`
          : ''
        return {
          keyToMap: 'monthStatus',
          queryKey: [
            AVAILABILITY_KEYS.monthStatusExtended(
              month,
              selectedDoctorId || undefined,
            ),
          ],
          endpoint: [
            'api',
            `availability/month-status-extended/${month}${queryParams}`,
          ],
          enabled: !!month,
        }
      },
    },
    {
      key: 'dailyDocuments',
      type: 'query',
      queryConfig: ({ get }) => {
        const { selectedDate } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        return {
          queryKey: ['documents/daily', selectedDate],
          endpoint: [
            'api',
            `documents?agendaDate=${selectedDate}&type=documenti`,
          ],
          enabled: userData?.role === 'admin' && !!selectedDate,
        }
      },
    },
    {
      key: 'patientsForExport',
      type: 'query',
      queryConfig: ({ get }) => {
        const ids = get('state', 'exportPatientIds', [])
        return {
          queryKey: ['patients/export-batch', ids.join(',')],
          endpoint: ['api', `registry/patients/batch?ids=${ids.join(',')}`],
          enabled: ids.length > 0,
        }
      },
    },
    {
      key: 'jobRolesForExport',
      type: 'query',
      queryConfig: {
        queryKey: [JOB_ROLES_KEYS.list()],
        endpoint: ['api', 'registry/job-roles'],
        enabled: true,
      },
    },
    {
      key: 'siteInspectionsForExport',
      type: 'query',
      queryConfig: ({ get }) => {
        const { selectedDate } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        return {
          queryKey: ['site-inspections/by-date', selectedDate],
          endpoint: [
            'api',
            `registry/site-inspections/by-date?date=${selectedDate}`,
          ],
          enabled: !!selectedDate,
        }
      },
    },
    {
      key: 'companyUsers',
      type: 'query',
      queryConfig: {
        queryKey: [USER_KEYS.list()],
        endpoint: ['api', 'users'],
        enabled: true,
      },
    },
    {
      key: 'updateAppointmentStatus',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const filters = get('state', 'filters', bookingsVariables.filters)
        const { selectedDate } = filters
        return {
          endpoint: ['api', 'appointments/:id/status'],
          method: 'PUT',
          queryKeyToInvalidate: [
            AVAILABILITY_KEYS.slots.multiDoctor(selectedDate),
            BOOKINGS_KEYS.summary.daily(selectedDate),
            BOOKINGS_KEYS.list(filters),
          ],
        }
      },
    },
    {
      key: 'updateAppointmentAcceptance',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const filters = get('state', 'filters', bookingsVariables.filters)
        const { selectedDate } = filters
        return {
          endpoint: ['api', 'appointments/:id/acceptance'],
          method: 'PUT',
          queryKeyToInvalidate: [
            AVAILABILITY_KEYS.slots.multiDoctor(selectedDate),
            BOOKINGS_KEYS.summary.daily(selectedDate),
            BOOKINGS_KEYS.list(filters),
          ],
        }
      },
    },
    {
      key: 'removeAppointmentIdoneity',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const filters = get('state', 'filters', bookingsVariables.filters)
        const { selectedDate } = filters
        return {
          endpoint: ['api', 'appointments/:id/idoneity'],
          method: 'DELETE',
          queryKeyToInvalidate: [
            AVAILABILITY_KEYS.slots.multiDoctor(selectedDate),
            BOOKINGS_KEYS.summary.daily(selectedDate),
            BOOKINGS_KEYS.list(filters),
          ],
        }
      },
    },
    {
      key: 'markAllAppointmentsCompleted',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const filters = get('state', 'filters', bookingsVariables.filters)
        const { selectedDate } = filters
        return {
          endpoint: ['api', 'appointments/mark-all-completed/:date'],
          method: 'PUT',
          queryKeyToInvalidate: [
            AVAILABILITY_KEYS.slots.multiDoctor(selectedDate),
            BOOKINGS_KEYS.summary.daily(selectedDate),
            BOOKINGS_KEYS.list(filters),
          ],
        }
      },
    },
    {
      key: 'sendPatientReminder',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'appointments/:id/remind-patient'],
        method: 'POST',
      },
    },
    {
      key: 'sendCompanyReminder',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'appointments/:id/remind-company'],
        method: 'POST',
      },
    },
    {
      key: 'sendCompanyConfirmation',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'appointments/:id/send-company-confirmation'],
        method: 'POST',
      },
    },
    {
      key: 'sendPatientDataReminder',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'appointments/:id/remind-patient-data'],
        method: 'POST',
      },
    },
    {
      key: 'sendCompanyConfirmationRequest',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'appointments/:id/request-company-confirmation'],
        method: 'POST',
      },
    },
    {
      key: 'sendNoShowNotification',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'appointments/:id/notify-no-show'],
        method: 'POST',
      },
    },
    {
      key: 'sendDoctorAbsenceNotification',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'appointments/notify-doctor-absence'],
        method: 'POST',
      },
    },
    {
      key: 'uploadIdoneity',
      type: 'mutation',
      mutationConfig: {
        endpoint: ['api', 'appointments/:appointmentId/idoneity'],
        method: 'POST',
        customRequest: uploadIdoneity,
      },
    },
    {
      key: 'uploadDailyDocuments',
      type: 'mutation',
      mutationConfig: ({ get }) => {
        const { selectedDate } = get(
          'state',
          'filters',
          bookingsVariables.filters,
        )
        return {
          endpoint: ['api', 'documents'],
          method: 'POST',
          customRequest: uploadDailyDocuments,
          queryKeyToInvalidate: ['documents/daily', selectedDate],
        }
      },
    },
  ]
  return queries
}

export interface DoctorDayInfo {
  doctorId: string
  color: string
  visitType: 'companyVisit' | 'siteInspection' | 'region' | 'default'
  companyName?: string
}

export interface ExtendedMonthStatus {
  status: 'available' | 'booked' | 'blocked'
  doctorCount: number
  doctors: Array<DoctorDayInfo>
  extraLocation?: string
}
